import React, { useState, useEffect, useRef } from 'react';
import { Card, Avatar, Input, Button, Badge } from '@/components/ui';
import { useAuth } from '@/core/auth/AuthContext';
import { useUserProfile, useUpdateProfile, useUserDevices, useLogoutDevices, useUpdatePassword, useUploadAvatar } from './hooks/useUserProfile';
import { getRoleBadgeClasses, getRoleLabel, getRoleIcon } from '@/utils/role';
import imageCompression from 'browser-image-compression';
import { toast } from 'sonner';
import { Camera, Check, Eye, EyeOff, ShieldCheck, Mail, Smartphone, Monitor, Laptop, LogOut, Loader2, User, Phone, Briefcase, Calendar, Info, ChevronRight, Lock, MoreVertical } from 'lucide-react';
import { cn } from '@/utils/cn';
import { PageHeaderNew } from '@/components/ui/PageHeaderNew';

export function UserProfilePage() {
  const { profile, refreshProfile } = useAuth();
  const { data: userDetail, isLoading: isLoadingProfile } = useUserProfile(profile?.id);
  const { mutateAsync: updateProfile, isPending } = useUpdateProfile();
  const { mutateAsync: uploadAvatar } = useUploadAvatar();

  const { data: devices = [], isLoading: isLoadingDevices } = useUserDevices(profile?.id);
  const { mutateAsync: logoutDevices, isPending: isLoggingOut } = useLogoutDevices();
  const { mutateAsync: changePassword, isPending: isChangingPassword } = useUpdatePassword();

  const [formData, setFormData] = useState({
    full_name: '',
    job_title: '',
    phone: '',
    email: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        job_title: profile.job_title || '',
        phone: profile.phone || '',
        email: profile.email || '',
      });
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) return;

    try {
      await updateProfile({
        id: profile.id,
        updates: {
          full_name: formData.full_name,
          job_title: formData.job_title,
          phone: formData.phone,
        }
      });
      await refreshProfile();
      toast.success('Cập nhật thông tin thành công!');
    } catch (error) {
      toast.error('Lỗi khi cập nhật hồ sơ');
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasMinLength || !hasUpperLower || !hasNumber || !hasSpecial || passwordForm.new !== passwordForm.confirm) return;

    try {
      await changePassword({ current: passwordForm.current, newPass: passwordForm.new });
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleUnderDevelopment = () => {
    toast.info('Tính năng này đang được phát triển và sẽ sớm ra mắt!');
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.id) return;

    if (file.size > 15 * 1024 * 1024) {
      toast.error('Kích thước ảnh quá lớn, tối đa hỗ trợ là 15MB');
      return;
    }

    try {
      setIsUploadingAvatar(true);
      toast.loading('Đang nén và tối ưu hóa ảnh...', { id: 'upload-avatar' });

      // Compression options
      const options = {
        maxSizeMB: 0.2, // Ép dung lượng tối đa 200KB
        maxWidthOrHeight: 500, // Resize chiều dài nhất về 500px (đủ nét cho avatar)
        useWebWorker: true,
        fileType: 'image/webp' // Convert mọi định dạng sang WebP
      };

      const compressedFile = await imageCompression(file, options);
      console.log(`Original: ${file.size / 1024}KB, Compressed: ${compressedFile.size / 1024}KB`);

      const publicUrl = await uploadAvatar({
        userId: profile.id,
        companyId: profile.company_id || 'f5bdf31a-bd43-439e-b4a5-dabf100cf37d',
        file: compressedFile
      });

      await updateProfile({ id: profile.id, updates: { avatar_path: publicUrl } });
      await refreshProfile();
      toast.success('Đã nén và cập nhật ảnh đại diện thành công!', { id: 'upload-avatar' });
    } catch (error: any) {
      toast.error('Lỗi khi xử lý ảnh đại diện: ' + error.message, { id: 'upload-avatar' });
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Password validation checks
  const hasMinLength = passwordForm.new.length >= 8;
  const hasUpperLower = /[a-z]/.test(passwordForm.new) && /[A-Z]/.test(passwordForm.new);
  const hasNumber = /[0-9]/.test(passwordForm.new);
  const hasSpecial = /[^A-Za-z0-9]/.test(passwordForm.new);

  return (
    <div className="flex flex-col gap-6 animate-in fade-in duration-500">
      <PageHeaderNew 
        title="Hồ sơ của tôi"
        subtitle="Quản lý thông tin cá nhân và thiết lập bảo mật tài khoản"
        backUrl="/dashboard"
      />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 items-start">

        {/* LEFT COLUMN */}
        <div className="space-y-6">

          {/* Card 1: Thông tin cá nhân */}
          <Card className="p-6 rounded-md border-slate-100 shadow-sm bg-white overflow-hidden relative">
            <div className="absolute top-0 left-0 w-1 h-full bg-emerald-600" />
            <h3 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide mb-6 flex items-center gap-2">
              <User className="w-4 h-4" />
              Thông tin cá nhân
            </h3>
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex flex-col items-center gap-3 shrink-0">
                <span className="sm:text-sm text-xs text-slate-500 self-start mb-1">Ảnh đại diện</span>
                <div className="relative group">
                  <Avatar
                    src={profile?.avatar_path || undefined}
                    name={profile?.full_name}
                    size="3xl"
                    className="shadow-sm"
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleAvatarChange}
                    accept="image/jpeg, image/png, image/webp"
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingAvatar}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-600 hover:text-emerald-600 hover:border-emerald-200 shadow-sm transition-colors z-10 disabled:opacity-50"
                  >
                    {isUploadingAvatar ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-2">Định dạng JPG, PNG. Tối đa 2MB.</p>
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <div className="space-y-1.5">
                  <label className="sm:text-sm text-xs text-slate-700">Họ và tên <span className="text-rose-500">*</span></label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="sm:text-sm text-xs border-slate-200 focus:border-emerald-500 rounded-lg h-10"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="sm:text-sm text-xs text-slate-700">Email <span className="text-rose-500">*</span></label>
                  <Input
                    value={formData.email}
                    disabled
                    className="sm:text-sm text-xs border-slate-200 bg-slate-50 text-slate-600 rounded-lg h-10"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="sm:text-sm text-xs text-slate-700">Số điện thoại</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="sm:text-sm text-xs border-slate-200 focus:border-emerald-500 rounded-lg h-10"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="sm:text-sm text-xs text-slate-700">Chức vụ</label>
                  <Input
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    className="sm:text-sm text-xs border-slate-200 focus:border-emerald-500 rounded-lg h-10"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-slate-50 flex justify-end">
              <Button
                onClick={handleSubmit}
                disabled={isPending}
                className="bg-emerald-700 hover:bg-emerald-800 text-white px-8 h-10 rounded-md sm:text-sm text-xs font-normal shadow-md shadow-emerald-700/10 transition-all active:scale-95"
              >
                {isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Lưu thay đổi
              </Button>
            </div>
          </Card>

          {/* Card 2: Đổi mật khẩu */}
          <Card className="p-6 rounded-md border-slate-100 shadow-sm bg-white">
            <h3 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide mb-6 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Đổi mật khẩu
            </h3>
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div className="space-y-1.5 max-w-md">
                <label className="sm:text-sm text-xs text-slate-700">Mật khẩu hiện tại <span className="text-rose-500">*</span></label>
                <div className="relative">
                  <Input
                    type={showPassword.current ? "text" : "password"}
                    value={passwordForm.current}
                    onChange={e => setPasswordForm({ ...passwordForm, current: e.target.value })}
                    placeholder="Nhập mật khẩu hiện tại"
                    className="sm:text-sm text-xs border-slate-200 rounded-lg h-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword.current ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1.5">
                  <label className="sm:text-sm text-xs text-slate-700">Mật khẩu mới <span className="text-rose-500">*</span></label>
                  <div className="relative">
                    <Input
                      type={showPassword.new ? "text" : "password"}
                      value={passwordForm.new}
                      onChange={e => setPasswordForm({ ...passwordForm, new: e.target.value })}
                      placeholder="Nhập mật khẩu mới"
                      className="sm:text-sm text-xs border-slate-200 rounded-lg h-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword.new ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="sm:text-sm text-xs text-slate-700">Xác nhận mật khẩu mới <span className="text-rose-500">*</span></label>
                  <div className="relative">
                    <Input
                      type={showPassword.confirm ? "text" : "password"}
                      value={passwordForm.confirm}
                      onChange={e => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                      placeholder="Nhập lại mật khẩu mới"
                      className="sm:text-sm text-xs border-slate-200 rounded-lg h-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                <div className="space-y-2 sm:text-sm text-xs">
                  <p className="text-slate-700 mb-1">Mật khẩu mới phải có:</p>
                  <div className={cn("flex items-center gap-2", hasMinLength ? "text-emerald-600" : "text-slate-400")}>
                    <Check size={14} /> Tối thiểu 8 ký tự
                  </div>
                  <div className={cn("flex items-center gap-2", hasUpperLower ? "text-emerald-600" : "text-slate-400")}>
                    <Check size={14} /> Bao gồm chữ hoa và chữ thường
                  </div>
                  <div className={cn("flex items-center gap-2", hasNumber ? "text-emerald-600" : "text-slate-400")}>
                    <Check size={14} /> Bao gồm số
                  </div>
                  <div className={cn("flex items-center gap-2", hasSpecial ? "text-emerald-600" : "text-slate-400")}>
                    <Check size={14} /> Bao gồm ký tự đặc biệt (ví dụ: !@#...)
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isChangingPassword || !passwordForm.current || !passwordForm.new || passwordForm.new !== passwordForm.confirm || !hasMinLength || !hasUpperLower || !hasNumber || !hasSpecial}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white px-6 h-10 rounded-md sm:text-sm text-xs font-normal shadow-md shadow-emerald-700/10 transition-all active:scale-95 whitespace-nowrap disabled:opacity-50"
                >
                  {isChangingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Cập nhật mật khẩu
                </Button>
              </div>
            </form>
          </Card>

          {/* Card 3: Thông tin tài khoản */}
          <Card className="p-6 rounded-md border-slate-100 shadow-sm bg-white">
            <h3 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide mb-6 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Thông tin tài khoản
            </h3>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Tên đăng nhập</span>
                <span className="text-slate-900">{profile?.email}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Ngày tạo tài khoản</span>
                <span className="text-slate-900">
                  {userDetail?.created_at ? new Date(userDetail.created_at).toLocaleString('vi-VN') : '01/03/2024 08:30'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-slate-600">Lần đăng nhập cuối</span>
                <span className="text-slate-900">
                  {devices.length > 0 ? new Date(devices[0].last_active).toLocaleString('vi-VN') : 'Chưa xác định'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-slate-600">Trạng thái tài khoản</span>
                <Badge variant={profile?.is_active ? 'premium-emerald' : 'premium-rose'} className="text-[10px] font-normal px-2">
                  {profile?.is_active ? 'Đang hoạt động' : 'Tạm khóa'}
                </Badge>
              </div>
            </div>
          </Card>

        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">

          {/* Card 4: Bảo mật tài khoản */}
          <Card className="p-6 rounded-md border-slate-100 shadow-sm bg-white">
            <h3 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide mb-6 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Bảo mật tài khoản
            </h3>

            <div className="space-y-6">
              {/* 2FA */}
              <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                  <ShieldCheck size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="sm:text-sm text-xs font-normal text-slate-900">Xác thực 2 lớp (2FA)</h4>
                    <span className="text-[10px] text-rose-500 mr-4 mt-1">Đang tắt</span>
                  </div>
                  <p className="sm:text-sm text-xs text-slate-500 pr-4 mb-3 leading-relaxed">
                    Tăng cường bảo mật tài khoản của bạn bằng xác thực 2 lớp.
                  </p>
                  <Button variant="outline" className="h-8 px-4 sm:text-sm text-xs font-normal rounded-lg ml-auto block" onClick={handleUnderDevelopment}>Bật</Button>
                </div>
              </div>

              {/* Email khôi phục */}
              <div className="flex items-start gap-4 pb-6 border-b border-slate-100">
                <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                  <Mail size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="sm:text-sm text-xs font-normal text-slate-900 mb-1">Email khôi phục</h4>
                  <p className="sm:text-sm text-xs text-slate-500 pr-4 mb-3 leading-relaxed">
                    Dùng để khôi phục mật khẩu khi cần thiết.
                  </p>
                  <p className="sm:text-sm text-xs text-slate-700 mb-3">an.nguyen***@gmail.com</p>
                  <Button variant="outline" className="h-8 px-4 sm:text-sm text-xs font-normal rounded-lg ml-auto block" onClick={handleUnderDevelopment}>Thay đổi</Button>
                </div>
              </div>

              {/* SDT khôi phục */}
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                  <Smartphone size={20} />
                </div>
                <div className="flex-1">
                  <h4 className="sm:text-sm text-xs font-normal text-slate-900 mb-1">Số điện thoại khôi phục</h4>
                  <p className="sm:text-sm text-xs text-slate-500 pr-4 mb-2 leading-relaxed">
                    Dùng để nhận mã xác thực và khôi phục mật khẩu.
                  </p>
                  <p className="sm:text-sm text-xs text-slate-700 mb-3">0901 *** 567</p>
                  <Button variant="outline" className="h-8 px-4 sm:text-sm text-xs font-normal rounded-lg ml-auto block" onClick={handleUnderDevelopment}>Thay đổi</Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Card 5: Thiết bị đăng nhập */}
          <Card className="p-6 rounded-md border-slate-100 shadow-sm bg-white">
            <h3 className="sm:text-sm text-xs font-semibold text-emerald-900 tracking-wide mb-1 flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Thiết bị đăng nhập
            </h3>
            <p className="text-[11px] text-slate-500 mb-6">Quản lý các thiết bị đã truy cập tài khoản</p>

            <div className="space-y-4 mb-6">
              {isLoadingDevices ? (
                <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : devices.length === 0 ? (
                <div className="text-center py-4 sm:text-sm text-xs text-slate-500">Chưa có dữ liệu thiết bị</div>
              ) : devices.map((dev: any) => {
                const isMobile = dev.device_name.toLowerCase().includes('iphone') || dev.device_name.toLowerCase().includes('ipad') || dev.device_name.toLowerCase().includes('android');
                const isMac = dev.device_name.toLowerCase().includes('mac');
                const DeviceIcon = isMobile ? Smartphone : (isMac ? Laptop : Monitor);

                return (
                  <div key={dev.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0 group">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-slate-50 flex items-center justify-center shrink-0">
                        <DeviceIcon size={18} className={dev.is_current_session ? "text-emerald-600" : "text-slate-400"} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="sm:text-sm text-xs font-normal text-slate-900">{dev.device_name}</span>
                          {dev.is_current_session && (
                            <Badge variant="glow-success" className="text-[9px] px-1.5 py-0 border-0">Hiện tại</Badge>
                          )}
                        </div>
                        <p className="sm:text-sm text-xs text-slate-500 mt-0.5">{dev.location}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="sm:text-sm text-xs text-slate-500 hidden sm:inline-block">{new Date(dev.last_active).toLocaleString('vi-VN')}</span>
                      {!dev.is_current_session && (
                        <button className="text-slate-400 hover:text-slate-700 w-6 h-6 flex items-center justify-center rounded hover:bg-slate-100 transition-colors">
                          <MoreVertical size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <Button
              variant="outline"
              className="w-full sm:text-sm text-xs text-rose-600 border-rose-100 hover:bg-rose-50 hover:text-rose-700 font-normal h-10 rounded-md disabled:opacity-50 transition-all"
              onClick={() => {
                if (profile?.id) logoutDevices(profile.id);
              }}
              disabled={isLoggingOut || devices.length <= 1}
            >
              {isLoggingOut ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Đăng xuất tất cả thiết bị khác
            </Button>
          </Card>

        </div>
      </div>
    </div>
  );
}

export default UserProfilePage;
